import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  FaLeaf,
  FaSpa,
  FaUserAlt,
  FaWater,
  FaRegSun,
  FaRegSmile,
  FaRegGrin,
} from "react-icons/fa";
import "./shopbyconcern.css";

const ShopByConcern = () => {
  const [selectedConcern, setSelectedConcern] = useState(null);
  const [activeProductId, setActiveProductId] = useState(null);
  const productState = useSelector((state) => state.products);
  const { loading, error, products = [] } = productState;
  const navigate = useNavigate();

  // ✅ Get unique concerns (bodyTypes array)
  const allConcerns = useMemo(() => {
    const concerns = new Set();
    products.forEach((product) => {
      if (product.bodyTypes && Array.isArray(product.bodyTypes)) {
        product.bodyTypes.forEach((type) => {
          if (type && type.trim()) concerns.add(type.trim());
        });
      }
    });
    return Array.from(concerns);
  }, [products]);

  // ✅ Icons & Colors
  const concernIcons = {
    "All skin types": { icon: <FaLeaf />, color: "#4caf50" },
    "All Skin Types": { icon: <FaLeaf />, color: "#4caf50" },
    "Dry Skin": { icon: <FaWater />, color: "#2196f3" },
    "Oily Skin": { icon: <FaLeaf />, color: "#ff5722" },
    "Acne": { icon: <FaUserAlt />, color: "#f44336" },
    "Dull Skin": { icon: <FaRegSun />, color: "#ff9800" },
    "Hair Fall": { icon: <FaRegSmile />, color: "#9c27b0" },
    "Sensitive Skin": { icon: <FaSpa />, color: "#8bc34a" },
    "Face Glow": { icon: <FaRegGrin />, color: "#ffeb3b" },
    "Tanning Remover": { icon: <FaRegSun />, color: "#ff9800" },
    "Face Brightening": { icon: <FaRegSun />, color: "#ff9800" },
    "Anti-aging": { icon: <FaSpa />, color: "#9c27b0" },
    "Dark Circles": { icon: <FaUserAlt />, color: "#f44336" },
    "Glowing Skin": { icon: <FaRegSun />, color: "#ffeb3b" },
    "Uneven Tone": { icon: <FaRegSmile />, color: "#ff5722" },
  };

  // ✅ Filter products
  const filteredProducts = selectedConcern
    ? products.filter((product) =>
        product.bodyTypes
          ?.map((type) => type.trim().toLowerCase())
          .includes(selectedConcern.toLowerCase())
      )
    : products;

  // ✅ Add to cart
  async function handleAddToCart(itemId) {
    const userData = localStorage.getItem("userDetails");
    if (!userData) {
      alert("Please log in first.");
      navigate("/login");
      return;
    }

    const userId = JSON.parse(userData)._id;
    try {
      const res = await fetch("https://swarna-herbal-backend.onrender.com/api/cart/addItem", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, itemId: String(itemId), quantity: 1 }),
      });

      const data = await res.json();
      if (res.ok) {
        alert("Item added to your cart!");
      } else {
        console.error("Error:", data.message);
        alert("Failed to add item");
      }
    } catch (err) {
      console.error(err);
      alert("Something went wrong. Please try again.");
    }
  }

  return (
    <div className="shop-by-concern">
      <h2 className="page-title">Shop by Concern</h2>

      {/* ✅ Concern Buttons */}
      <div className="concern-categories">
        {loading ? (
          <p>Loading concerns...</p>
        ) : allConcerns.length > 0 ? (
          allConcerns.map((concern, index) => (
            <button
              key={index}
              className={`concern-btn ${
                selectedConcern === concern ? "active" : ""
              }`}
              onClick={() => setSelectedConcern(concern)}
              style={{ color: concernIcons[concern]?.color || "#000" }}
            >
              {concernIcons[concern]?.icon && (
                <span
                  className="concern-icon"
                  style={{ color: concernIcons[concern]?.color }}
                >
                  {concernIcons[concern]?.icon}
                </span>
              )}
              {concern}
            </button>
          ))
        ) : (
          <p>No concerns found.</p>
        )}

        {/* Reset Filter */}
        <button
          className={`concern-btn ${!selectedConcern ? "active" : ""}`}
          onClick={() => setSelectedConcern(null)}
          style={{ color: "#000" }}
        >
          All Products
        </button>
      </div>

      {/* ✅ Product Grid */}
      <div className="product-cards">
        {loading ? (
          <p>Loading products...</p>
        ) : error ? (
          <p>Error fetching products.</p>
        ) : filteredProducts.length === 0 ? (
          <p>No products for this concern.</p>
        ) : (
          filteredProducts.map((product) => (
            <div key={product._id} className="product-card">
              <img
                src={`https://swarna-herbal-backend.onrender.com/uploads/${product.image}`}
                alt={product.name}
                className="product-image"
              />
              <div className="product-info">
                <h4>{product.name}</h4>
                <p>Price - ₹{product.price}</p>
                <p>Offer Price - ₹{product.offerPrice}</p>

                <p className="product-short-description">
                  {product.description.length > 150
                    ? `${product.description.slice(0, 150)}...`
                    : product.description}
                </p>

                {product.description.length > 150 && (
                  <button
                    className="read-more-btn"
                    onClick={() => setActiveProductId(product._id)}
                  >
                    Read More
                  </button>
                )}

                <button
                  style={{ background: "green" }}
                  className="product-btn"
                  onClick={() => handleAddToCart(product._id)}
                >
                  Add To Cart
                </button>
                <button
                  className="product-btn"
                  onClick={() => navigate(`/order/${product._id}`)}
                >
                  Buy Now
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* ✅ Modal */}
      {activeProductId && (
        <div className="modal-overlay" onClick={() => setActiveProductId(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              style={{ background: "red", color: "white" }}
              className="close-modal-btn"
              onClick={() => setActiveProductId(null)}
            >
              ×
            </button>
            {filteredProducts
              .filter((product) => product._id === activeProductId)
              .map((product) => (
                <div key={product._id}>
                  <img
                    src={`https://swarna-herbal-backend.onrender.com/uploads/${product.image}`}
                    alt={product.name}
                    className="modal-image"
                  />
                  <h3>{product.name}</h3>
                  <p>{product.description}</p>
                  <p>Price - ₹{product.price}</p>
                  <p>Offer Price - ₹{product.offerPrice}</p>
                  <button
                    style={{ background: "green" }}
                    className="product-btn"
                    onClick={() => handleAddToCart(product._id)}
                  >
                    Add To Cart
                  </button>
                  <button
                    className="product-btn"
                    onClick={() => navigate(`/order/${product._id}`)}
                  >
                    Buy Now
                  </button>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopByConcern;
